import React, { useState, useEffect } from 'react';
import api from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { ArrowRightLeft, RefreshCw, TrendingUp, Globe, Search } from 'lucide-react';
import { toast } from 'sonner';

const Currency = () => {
  const [currencies, setCurrencies] = useState([]);
  const [rates, setRates] = useState(null);
  const [loading, setLoading] = useState(true);
  const [converting, setConverting] = useState(false);
  
  // Conversion state
  const [fromCurrency, setFromCurrency] = useState('USD');
  const [toCurrency, setToCurrency] = useState('ETB');
  const [amount, setAmount] = useState('100');
  const [result, setResult] = useState(null);
  
  // Rates filter
  const [baseCurrency, setBaseCurrency] = useState('USD');
  const [ratesSearch, setRatesSearch] = useState('');

  useEffect(() => {
    loadCurrencies();
  }, []);

  useEffect(() => {
    if (baseCurrency) {
      loadRates();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [baseCurrency]);

  const loadCurrencies = async () => {
    try {
      const res = await api.get('/currency/supported');
      setCurrencies(res.data);
    } catch (error) {
      toast.error('Failed to load currencies');
    }
  };

  const loadRates = async () => {
    setLoading(true);
    try {
      const res = await api.get(`/currency/rates/${baseCurrency}`);
      setRates(res.data);
    } catch (error) {
      toast.error('Failed to load exchange rates');
    } finally {
      setLoading(false);
    }
  };

  const handleConvert = async () => {
    if (!amount || parseFloat(amount) <= 0) {
      toast.error('Please enter a valid amount');
      return;
    }

    setConverting(true);
    try {
      const res = await api.post('/currency/convert', {
        amount: parseFloat(amount),
        from_currency: fromCurrency,
        to_currency: toCurrency
      });
      setResult(res.data);
    } catch (error) {
      toast.error('Failed to convert currency');
    } finally {
      setConverting(false);
    }
  };

  const swapCurrencies = () => {
    const temp = fromCurrency;
    setFromCurrency(toCurrency);
    setToCurrency(temp);
    setResult(null);
  };

  const getCurrencySymbol = (code) => {
    const currency = currencies.find(c => c.code === code);
    return currency?.symbol || code;
  };

  const getCurrencyName = (code) => {
    const currency = currencies.find(c => c.code === code);
    return currency?.name || code;
  };

  // Filter rates for display
  const filteredRates = rates?.rates
    ? Object.entries(rates.rates)
        .filter(([code]) => {
          if (!ratesSearch) return true;
          const currency = currencies.find(c => c.code === code);
          const searchLower = ratesSearch.toLowerCase();
          return code.toLowerCase().includes(searchLower) ||
                 (currency?.name?.toLowerCase().includes(searchLower));
        })
        .sort((a, b) => a[0].localeCompare(b[0]))
    : [];

  return (
    <div className="space-y-6" data-testid="currency-page">
      <div>
        <h1 className="text-2xl font-bold text-[hsl(var(--ink-1))]" style={{ fontFamily: 'Space Grotesk' }}>
          Currency & Exchange Rates
        </h1>
        <p className="text-[hsl(var(--muted-ink))]">
          Convert currencies and view live exchange rates
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Currency Converter */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <ArrowRightLeft size={20} className="text-[hsl(var(--brand-500))]" />
              Currency Converter
            </CardTitle>
            <CardDescription>
              Convert between {currencies.length}+ supported currencies
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-[1fr,auto,1fr] gap-2 items-end">
              <div>
                <Label>From</Label>
                <Select value={fromCurrency} onValueChange={(v) => { setFromCurrency(v); setResult(null); }}>
                  <SelectTrigger className="mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="max-h-[300px]">
                    {currencies.map(c => (
                      <SelectItem key={c.code} value={c.code}>
                        {c.symbol} {c.code} - {c.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <Button
                variant="outline"
                size="icon"
                onClick={swapCurrencies}
                className="mb-0.5"
              >
                <ArrowRightLeft size={16} />
              </Button>
              
              <div>
                <Label>To</Label>
                <Select value={toCurrency} onValueChange={(v) => { setToCurrency(v); setResult(null); }}>
                  <SelectTrigger className="mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="max-h-[300px]">
                    {currencies.map(c => (
                      <SelectItem key={c.code} value={c.code}>
                        {c.symbol} {c.code} - {c.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <Label>Amount</Label>
              <Input
                type="number"
                value={amount}
                onChange={(e) => { setAmount(e.target.value); setResult(null); }}
                placeholder="Enter amount"
                className="mt-1"
              />
            </div>

            <Button
              onClick={handleConvert}
              disabled={converting}
              className="w-full bg-[hsl(var(--brand-500))] hover:bg-[hsl(var(--brand-600))] text-white"
            >
              {converting ? (
                <>
                  <RefreshCw size={16} className="mr-2 animate-spin" />
                  Converting...
                </>
              ) : (
                <>
                  <TrendingUp size={16} className="mr-2" />
                  Convert
                </>
              )}
            </Button>

            {result && (
              <div className="mt-4 p-4 bg-[hsl(var(--brand-50))] rounded-lg">
                <div className="text-center">
                  <p className="text-sm text-[hsl(var(--muted-ink))]">
                    {getCurrencySymbol(result.from_currency)} {result.original_amount.toLocaleString()} {result.from_currency}
                  </p>
                  <p className="text-3xl font-bold text-[hsl(var(--brand-600))] my-2">
                    {getCurrencySymbol(result.to_currency)} {result.converted_amount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </p>
                  <p className="text-sm text-[hsl(var(--muted-ink))]">
                    {getCurrencyName(result.to_currency)}
                  </p>
                  <p className="text-xs text-gray-500 mt-2">
                    Rate: 1 {result.from_currency} = {result.exchange_rate.toFixed(4)} {result.to_currency}
                  </p>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Quick Conversions */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Globe size={20} className="text-[hsl(var(--brand-500))]" />
              Popular Conversions
            </CardTitle>
            <CardDescription>
              Quick reference for common currency pairs
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {[
                { from: 'USD', to: 'ETB', label: 'US Dollar → Ethiopian Birr' },
                { from: 'USD', to: 'EUR', label: 'US Dollar → Euro' },
                { from: 'USD', to: 'GBP', label: 'US Dollar → British Pound' },
                { from: 'EUR', to: 'USD', label: 'Euro → US Dollar' },
                { from: 'GBP', to: 'USD', label: 'British Pound → US Dollar' },
                { from: 'USD', to: 'KES', label: 'US Dollar → Kenyan Shilling' },
                { from: 'USD', to: 'NGN', label: 'US Dollar → Nigerian Naira' },
                { from: 'EUR', to: 'ETB', label: 'Euro → Ethiopian Birr' },
              ].map((pair, i) => (
                <button
                  key={i}
                  onClick={() => {
                    setFromCurrency(pair.from);
                    setToCurrency(pair.to);
                    setResult(null);
                  }}
                  className="w-full text-left p-3 rounded-lg border hover:bg-gray-50 transition-colors flex justify-between items-center"
                >
                  <span className="text-sm">{pair.label}</span>
                  <span className="text-xs text-[hsl(var(--muted-ink))]">
                    {pair.from} → {pair.to}
                  </span>
                </button>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Exchange Rates Table */}
      <Card>
        <CardHeader>
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <CardTitle className="flex items-center gap-2">
                <TrendingUp size={20} className="text-[hsl(var(--brand-500))]" />
                Exchange Rates
              </CardTitle>
              <CardDescription>
                {rates?.last_update ? `Last updated: ${new Date(rates.last_update).toLocaleString()}` : 'Live exchange rates'}
              </CardDescription>
            </div>
            <div className="flex gap-2">
              <Select value={baseCurrency} onValueChange={setBaseCurrency}>
                <SelectTrigger className="w-[150px]">
                  <SelectValue placeholder="Base currency" />
                </SelectTrigger>
                <SelectContent>
                  {currencies.slice(0, 20).map(c => (
                    <SelectItem key={c.code} value={c.code}>
                      {c.code} - {c.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button variant="outline" onClick={loadRates} disabled={loading}>
                <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {/* Search */}
          <div className="mb-4 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={16} />
            <Input
              placeholder="Search currencies..."
              value={ratesSearch}
              onChange={(e) => setRatesSearch(e.target.value)}
              className="pl-10"
            />
          </div>

          {loading ? (
            <div className="text-center py-8 text-[hsl(var(--muted-ink))]">
              Loading exchange rates...
            </div>
          ) : (
            <div className="border rounded-lg overflow-hidden">
              <div className="max-h-[400px] overflow-y-auto">
                <Table>
                  <TableHeader className="sticky top-0 bg-white">
                    <TableRow>
                      <TableHead>Currency</TableHead>
                      <TableHead>Code</TableHead>
                      <TableHead className="text-right">Rate (1 {baseCurrency} =)</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredRates.map(([code, rate]) => {
                      const currency = currencies.find(c => c.code === code);
                      return (
                        <TableRow 
                          key={code}
                          className={code === 'ETB' ? 'bg-[hsl(var(--brand-50))]' : ''}
                        >
                          <TableCell>
                            <div className="flex items-center gap-2">
                              <span className="font-medium">{currency?.symbol || ''}</span>
                              <span>{currency?.name || code}</span>
                            </div>
                          </TableCell>
                          <TableCell className="font-mono">{code}</TableCell>
                          <TableCell className="text-right font-mono">
                            {typeof rate === 'number' ? rate.toFixed(4) : rate}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
            </div>
          )}

          {!loading && filteredRates.length === 0 && (
            <div className="text-center py-8 text-[hsl(var(--muted-ink))]">
              No currencies found matching your search
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default Currency;
